/**
 * Pure (DI-free, Angular-free) helpers for the WYSIWYG rich-text editor seam.
 *
 * These functions are the single place where the rich-text *string* shape is
 * interpreted. They are deliberately framework-free so they can be imported by:
 *   - the drop-in `RichTextFieldComponent` (display + value normalization), and
 *   - the DI-free pure sheet validators (emptiness checks) without tripping
 *     `npm run check:validator-purity` (no `@angular`, no `inject()`).
 *
 * ## The two string shapes we bridge
 *
 * 1. **Editor fragment** — what TinyMCE produces and what the frontend
 *    persists / sends: a plain HTML fragment, e.g. `<p>Hello</p><table>…</table>`.
 *    The backend (`MbrsHelper.BuildContent`) wraps this in
 *    `<qname contextRef="…">…</qname>` itself, so the frontend never builds the
 *    XBRL envelope.
 *
 * 2. **Legacy / backend full XHTML document** — existing values look like
 *    `<?xml version="1.0" ?><html xmlns="…"><head>…</head><body style="…">…</body></html>`
 *    (generated by SSM mTool / N8N, `TX27_HTM` generator). When we *load* such a
 *    value into the editor or preview we must unwrap the `<body>` inner HTML.
 *
 * Both shapes are `string`s — the form-control value type never changes.
 */

/**
 * Strip tags, comments, `&nbsp;`/whitespace entities and return the remaining
 * visible text. Used to decide whether rich-text content is "empty".
 *
 * Intentionally regex-based (not DOM-based) so it is safe in any context —
 * including the DI-free, possibly non-browser, pure validator path.
 */
function visibleText(html: string | null | undefined): string {
  if (html == null) return '';
  return String(html)
    // Drop XML prolog, comments, and head metadata that carry no visible text.
    .replace(/<\?[\s\S]*?\?>/g, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<head[\s\S]*?<\/head>/gi, '')
    // Strip all remaining tags.
    .replace(/<[^>]*>/g, '')
    // Common "blank" entities and unicode whitespace.
    .replace(/&nbsp;|&#160;|&#xa0;/gi, ' ')
    .replace(/ /g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * True when the HTML has no visible content — empty string, whitespace, or
 * "structural-only" markup like `<p></p>`, `<p>&nbsp;</p>`, or an empty doc.
 *
 * NOTE: a table/image with no text still counts as EMPTY here because this is a
 * required-field text check. Role 24's explanatory field is text content; if a
 * future field needs "a table alone is content", add a dedicated check rather
 * than loosening this one.
 */
export function isEmptyHtml(html: string | null | undefined): boolean {
  return visibleText(html) === '';
}

/**
 * Given a stored value, return the HTML suitable for *editing / previewing*.
 *
 * - Full XHTML document (`<html>…<body>…</body></html>`) → inner `<body>` HTML.
 * - Plain fragment → returned unchanged.
 * - Empty / nullish → `''`.
 *
 * This is the load-side unwrap for legacy/backend values. It is tolerant: if a
 * `<body>` open tag is found but no matching close tag, it returns everything
 * after the open tag.
 */
export function extractBodyHtml(stored: string | null | undefined): string {
  if (stored == null) return '';
  const value = String(stored);
  if (value.trim() === '') return '';

  // Only attempt to unwrap when this looks like a full document.
  if (!/<html[\s>]/i.test(value) && !/<body[\s>]/i.test(value)) {
    return value;
  }

  const bodyMatch = value.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) return bodyMatch[1].trim();

  // Unterminated <body> — take everything after the open tag.
  const openMatch = value.match(/<body[^>]*>([\s\S]*)$/i);
  if (openMatch) return openMatch[1].trim();

  return value;
}

/**
 * Normalize the editor's output into the value we *persist / send*.
 *
 * Currently: a trimmed HTML fragment, or **`''` when the content is empty**.
 *
 * Collapsing empty content to `''` is what makes `required` validation agree
 * across the two verdict paths with NO custom validator and NO pure-validator
 * change: the mounted component's `Validators.required` and the pure
 * `isEmptyCell('')` both treat `''` as empty.
 *
 * This is the single switch point if the backend contract ever requires the
 * full `<?xml?><html>…</html>` document instead of a fragment.
 */
export function normalizeFragment(editorHtml: string | null | undefined): string {
  if (isEmptyHtml(editorHtml)) return '';
  return String(editorHtml).trim();
}
